import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  X,
  Play,
  Clock,
  Download,
  ArrowRight,
  Folder,
  Image as ImageIcon,
  Search,
  FileText,
} from "lucide-react";
import "../Styles/Resources.scss";
import * as XLSX from "xlsx";
import BlurImage from "./BlurImage";

// Complete Master Resources Data Fallback
const defaultMasterResources = [
  {
    name: "EngageAI for Customer Service",
    type: "video",
    tag: "VIDEO",
    image: "EngageAI-for-Customer Service.mp4",
    duration: "12 min",
    size: "Video · 31.8 MB",
    desc: "Watch how EngageAI helps teams resolve queries faster and deliver exceptional support.",
    isHero: true,
  },
  {
    name: "InFlow EngageAI Solution Flyer V3.0",
    type: "pdf",
    tag: "PDF",
    image: "brochure/InFlow EngageAI Flyer V3.0.pdf",
    size: "PDF · 973 KB",
    desc: "Comprehensive enterprise feature matrix, channel adapters, and security architecture.",
  },
  {
    name: "Customer Service Brochure V3.0",
    type: "pdf",
    tag: "PDF",
    image: "brochure/Customer Service V3.0.pdf",
    size: "PDF · 1.7 MB",
    desc: "Detailed overview of customer service AI capabilities, multi-lingual voice bots, and automated ticketing.",
  },
  {
    name: "Sales Intelligence Brochure V3.0",
    type: "pdf",
    tag: "PDF",
    image: "brochure/Sales Intelligence V3.0.pdf",
    size: "PDF · 1.2 MB",
    desc: "Discover key sales capabilities, ROI metrics, and real business impact.",
  },
  {
    name: "Smart Ordering Brochure V3.0",
    type: "pdf",
    tag: "PDF",
    image: "brochure/Smart Ordering V3.0.pdf",
    size: "PDF · 840 KB",
    desc: "Automated ordering workflow, menu sync, and multi-channel conversational commerce.",
  },
  {
    name: "InFlow DocsAI Solution Flyer V3.0",
    type: "pdf",
    tag: "PDF",
    image: "brochure/DocsAI Flyer V3.0.pdf",
    size: "PDF · 1.0 MB",
    desc: "Next-gen intelligent document processing, multi-format parsing, and LLM-powered extraction.",
  },
  {
    name: "InFlow InsightsAI Solution Flyer V3.0",
    type: "pdf",
    tag: "PDF",
    image: "brochure/InsightsAI V3.0.pdf",
    size: "PDF · 665 KB",
    desc: "Natural language business intelligence, Text-to-SQL analytics, and automated dashboard generation.",
  },
  {
    name: "InFlow WhatsApp Calling Flyer V3.0",
    type: "pdf",
    tag: "PDF",
    image: "brochure/WhatsApp Calling Flyer V3.0.pdf",
    size: "PDF · 820 KB",
    desc: "Native WhatsApp voice calling, speech-to-text, and conversational AI agent orchestration.",
  },
  {
    name: "iNextLabs Corporate Overview Flyer V4",
    type: "pdf",
    tag: "PDF",
    image: "brochure/_iNextLabs Corporate Flyer V4_compressed.pdf",
    size: "PDF · 2.0 MB",
    desc: "Complete company overview, enterprise AI platform capabilities, and partner ecosystem.",
  },
  {
    name: "Voice Bot Architecture",
    type: "diagram",
    tag: "DIAGRAM",
    image: "architecture/STS Arch Diagram.png",
    size: "PNG · 277 KB",
    desc: "High-resolution architectural diagram of multi-modal voice processing topology.",
  },
  {
    name: "EngageAI Commercial Proposal",
    type: "doc",
    tag: "DOC",
    image: "documents/inFlowEngageAI-proposal.docx",
    size: "DOCX · 6.2 MB",
    desc: "Explore complete system architecture, commercial terms, and rollout roadmap.",
  },
];

// Safe Image & Video Path Resolver preserving directory slashes while encoding spaces
const getImagePath = (imageName) => {
  if (!imageName) return "";
  if (imageName.startsWith("http://") || imageName.startsWith("https://")) {
    return imageName;
  }
  const cleanPath = imageName.replace(/^\/+/, "").replace(/^images\//i, "");
  const encodedSegments = cleanPath.split("/").map(encodeURIComponent).join("/");
  return `/images/${encodedSegments}`;
};

// Helper to enrich raw objects with type, tag, size, and desc based on file extension
const enrichResource = (item) => {
  const img = (item.image || "").toLowerCase();
  let type = item.type;
  let tag = item.tag;

  if (!type) {
    if (img.endsWith(".mp4") || img.endsWith(".mov") || img.endsWith(".webm")) {
      type = "video";
      tag = "VIDEO";
    } else if (img.endsWith(".pdf")) {
      type = "pdf";
      tag = "PDF";
    } else if (img.endsWith(".docx") || img.endsWith(".doc") || img.endsWith(".xlsx")) {
      type = "doc";
      tag = img.endsWith(".xlsx") ? "EXCEL" : "DOC";
    } else if (img.endsWith(".png") || img.endsWith(".jpg") || img.endsWith(".jpeg") || img.endsWith(".svg")) {
      type = "diagram";
      tag = "DIAGRAM";
    } else {
      type = "doc";
      tag = "DOC";
    }
  }

  return {
    ...item,
    type,
    tag: tag || type.toUpperCase(),
    size: item.size || (type === "video" ? "Video · 24 MB" : type === "pdf" ? "PDF · 2.8 MB" : type === "doc" ? "DOCX · 1.2 MB" : "PNG · 320 KB"),
    desc: item.desc || item.alt || "Enterprise technical documentation and AI agent resources.",
    duration: item.duration || (type === "video" ? "10 min" : "")
  };
};

const Resources = ({ content = {} }) => {
  const { diagrams = [] } = content;
  const [selectedDiagram, setSelectedDiagram] = useState(null);
  const [isVaultOpen, setIsVaultOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Map and enrich master resource list
  const rawList =
    Array.isArray(diagrams) && diagrams.length > 0
      ? diagrams
      : defaultMasterResources;

  const allResourcesList = rawList.map(enrichResource);

  // Dynamic tab counts
  const videoCount = allResourcesList.filter((r) => r.type === "video").length;
  const pdfCount = allResourcesList.filter((r) => r.type === "pdf").length;
  const docCount = allResourcesList.filter((r) => r.type === "doc").length;
  const diagramCount = allResourcesList.filter((r) => r.type === "diagram").length;

  // Hero Featured Resource
  const heroResource =
    allResourcesList.find((r) => r.isHero) || allResourcesList[0];

  // 4 Grid Resources for initial section display
  const gridResources = allResourcesList
    .filter((r) => r.name !== heroResource.name)
    .slice(0, 4);

  // Vault Filtered List
  const filteredVaultResources = allResourcesList.filter((item) => {
    const matchesCategory =
      activeCategory === "all" ||
      (activeCategory === "videos" && item.type === "video") ||
      (activeCategory === "pdfs" && item.type === "pdf") ||
      (activeCategory === "docs" && item.type === "doc") ||
      (activeCategory === "diagrams" && item.type === "diagram");

    const matchesSearch =
      !searchQuery.trim() ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.desc.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesCategory && matchesSearch;
  });

  // Strict Body & Document Scroll Lock + Lenis Pause
  useEffect(() => {
    if (selectedDiagram || isVaultOpen) {
      const originalBodyOverflow = document.body.style.overflow;
      const originalDocOverflow = document.documentElement.style.overflow;

      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";

      if (window.lenis) {
        window.lenis.stop();
      }

      const handleKeyDown = (e) => {
        if (e.key === "Escape") {
          if (selectedDiagram) {
            closeModal();
          } else if (isVaultOpen) {
            setIsVaultOpen(false);
          }
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalDocOverflow;
        if (window.lenis) {
          window.lenis.start();
        }
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [selectedDiagram, isVaultOpen]);

  const openContent = async (diagram) => {
    const fileName = diagram.image || "";

    if (fileName.toLowerCase().endsWith(".docx")) {
      try {
        const link = document.createElement("a");
        link.href = getImagePath(fileName);
        link.download = fileName.split("/").pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error("Download failed:", err);
        setError("Failed to download Word document.");
        setSelectedDiagram(diagram);
      }
      return;
    }

    if (fileName.toLowerCase().endsWith(".pdf")) {
      window.open(getImagePath(fileName), "_blank");
      return;
    }

    setSelectedDiagram(diagram);
    setIsLoading(true);
    setError(null);
    setTableData([]);

    if (fileName.toLowerCase().endsWith(".xlsx")) {
      try {
        const response = await fetch(getImagePath(fileName));
        if (!response.ok) {
          throw new Error(
            `Failed to fetch file: ${response.status} ${response.statusText}`
          );
        }
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        setTableData(jsonData);
      } catch (err) {
        console.error("Error loading Excel file:", err);
        setError(`Failed to load Excel file: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedDiagram(null);
    setTableData([]);
    setError(null);
    setIsLoading(false);
  };

  return (
    <div className="resources-wrapper">
      <div className="resources-container">
        {/* Apple Editorial Header Meta */}
        <div className="resources-header-meta">
          <span className="resources-label-apple">RESOURCES</span>
          <h2 className="resources-main-title">Resources & Technical Library</h2>
          <p className="resources-main-sub">
            Access technical specifications, architectural diagrams, brochures, documents, and video walkthroughs.
          </p>
        </div>

        {/* Resources Layout: Content Column (Max-Width 63%) */}
        <div className="resources-main-split">
          <div className="resources-content-col">
            {/* Hero Featured Video Card */}
            <div
              className="resources-hero-featured-card"
              onClick={() => openContent(heroResource)}
            >
              <div className="hero-card-thumbnail">
                <div className="hero-card-bg-gradient">
                  <div className="hero-card-title-preview">
                    <span className="hero-card-brand">{heroResource.name}</span>
                    <p className="hero-card-micro">{heroResource.desc}</p>
                  </div>
                </div>
                <div className="hero-play-button">
                  {heroResource.type === "video" ? (
                    <Play size={22} fill="#F05742" color="#F05742" className="play-icon" />
                  ) : heroResource.type === "diagram" ? (
                    <ImageIcon size={22} color="#F05742" />
                  ) : heroResource.type === "pdf" ? (
                    <FileText size={22} color="#F05742" />
                  ) : (
                    <Download size={22} color="#F05742" />
                  )}
                </div>
              </div>

              <div className="hero-card-details">
                <span className="hero-card-badge">
                  {heroResource.type === "video"
                    ? "FEATURED VIDEO"
                    : heroResource.type === "diagram"
                    ? "FEATURED DIAGRAM"
                    : heroResource.type === "pdf"
                    ? "FEATURED PDF"
                    : "FEATURED RESOURCE"}
                </span>
                <h3 className="hero-card-title">{heroResource.name}</h3>
                <p className="hero-card-desc">{heroResource.desc}</p>
                <div className="hero-card-footer">
                  <span className="hero-card-meta">
                    {heroResource.type === "video" || heroResource.duration ? (
                      <>
                        <Clock size={14} />
                        <span>{heroResource.duration || "12 min"}</span>
                      </>
                    ) : (
                      <>
                        <Download size={14} />
                        <span>{heroResource.size}</span>
                      </>
                    )}
                  </span>
                  <span className="hero-card-cta">
                    <span>
                      {heroResource.type === "video"
                        ? "Watch now"
                        : heroResource.type === "diagram"
                        ? "View Diagram"
                        : heroResource.type === "pdf"
                        ? "View PDF"
                        : "View Document"}
                    </span>
                    <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </div>

            {/* 2x2 Resource Cards Grid */}
            <div className="resources-2x2-grid">
              {gridResources.map((item, idx) => (
                <div
                  key={item.name + idx}
                  className="resource-mini-card"
                  onClick={() => openContent(item)}
                >
                  <div className="mini-card-top">
                    <span className={`mini-card-badge ${item.type}`}>
                      {item.tag}
                    </span>
                  </div>

                  <h4 className="mini-card-title">{item.name}</h4>
                  <p className="mini-card-desc">{item.desc}</p>

                  <div className="mini-card-footer">
                    <span className="mini-card-meta">
                      {item.duration ? (
                        <>
                          <Clock size={13} />
                          <span>{item.duration}</span>
                        </>
                      ) : (
                        <>
                          <Download size={13} />
                          <span>{item.size}</span>
                        </>
                      )}
                    </span>
                    <ArrowRight size={14} className="mini-card-arrow" />
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Action Button opening Technical Vault Drawer */}
            <div className="resources-bottom-action">
              <button
                type="button"
                className="view-all-resources-btn"
                onClick={() => setIsVaultOpen(true)}
              >
                <Folder size={16} />
                <span>View all resources ({allResourcesList.length})</span>
                <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ALL 8 RESOURCES TECHNICAL VAULT DRAWER MODAL */}
      {isVaultOpen &&
        ReactDOM.createPortal(
          <div
            className="modal-overlay vault-overlay"
            onClick={() => setIsVaultOpen(false)}
          >
            <div
              className="modal-content vault-content"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Apple Editorial Modal Header */}
              <div className="modal-header vault-header">
                <div className="vault-header-title">
                  <div className="vault-icon-badge">
                    <Folder size={20} className="vault-icon-accent" />
                  </div>
                  <div>
                    <span className="vault-eyebrow">TECHNICAL VAULT</span>
                    <h3 className="vault-main-title">
                      Resource & Technical Vault ({allResourcesList.length})
                    </h3>
                    <p className="vault-sub-title">
                      Complete library of videos, PDF brochures, proposals, and architecture diagrams.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="close-button vault-close-btn"
                  onClick={() => setIsVaultOpen(false)}
                  aria-label="Close Vault"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Vault Toolbar */}
              <div className="vault-toolbar" onClick={(e) => e.stopPropagation()}>
                <div className="vault-search-box">
                  <Search size={16} className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search resources by title or keyword..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={() => setSearchQuery("")}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                <div className="vault-category-pills" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className={`vault-pill ${activeCategory === "all" ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveCategory("all");
                    }}
                  >
                    All ({allResourcesList.length})
                  </button>
                  <button
                    type="button"
                    className={`vault-pill ${activeCategory === "videos" ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveCategory("videos");
                    }}
                  >
                    Videos ({videoCount})
                  </button>
                  <button
                    type="button"
                    className={`vault-pill ${activeCategory === "pdfs" ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveCategory("pdfs");
                    }}
                  >
                    PDF Brochures ({pdfCount})
                  </button>
                  <button
                    type="button"
                    className={`vault-pill ${activeCategory === "docs" ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveCategory("docs");
                    }}
                  >
                    Documents ({docCount})
                  </button>
                  <button
                    type="button"
                    className={`vault-pill ${activeCategory === "diagrams" ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveCategory("diagrams");
                    }}
                  >
                    Diagrams ({diagramCount})
                  </button>
                </div>
              </div>

              {/* Vault Items Grid */}
              <div className="vault-grid">
                {filteredVaultResources.length > 0 ? (
                  filteredVaultResources.map((item, idx) => (
                    <div
                      key={item.name + idx}
                      className="vault-item-card"
                      onClick={() => openContent(item)}
                    >
                      <div className="vault-item-top">
                        <span className={`mini-card-badge ${item.type}`}>
                          {item.tag}
                        </span>
                        {item.duration ? (
                          <span className="vault-item-meta">
                            <Clock size={12} />
                            <span>{item.duration}</span>
                          </span>
                        ) : (
                          <span className="vault-item-meta">
                            <Download size={12} />
                            <span>{item.size}</span>
                          </span>
                        )}
                      </div>

                      <h4 className="vault-item-title">{item.name}</h4>
                      <p className="vault-item-desc">{item.desc}</p>

                      <div className="vault-item-footer">
                        <span className="vault-open-link">
                          <span>
                            {item.type === "video"
                              ? "Watch Video"
                              : item.type === "pdf"
                              ? "View PDF"
                              : item.type === "doc"
                              ? "Download Doc"
                              : "View Diagram"}
                          </span>
                          <ArrowRight size={13} />
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="vault-empty-state">
                    <FileText size={32} className="empty-icon" />
                    <p>No resources found matching your search.</p>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* SINGLE ITEM PREVIEW MODAL (VIDEO / DIAGRAM / EXCEL) */}
      {selectedDiagram &&
        ReactDOM.createPortal(
          <div
            className="modal-overlay preview-overlay"
            onClick={closeModal}
            data-lenis-prevent
          >
            <div
              className="modal-content preview-content"
              onClick={(e) => e.stopPropagation()}
              data-lenis-prevent
            >
              <div className="modal-header preview-modal-header">
                <div className="preview-header-meta">
                  <span className="preview-eyebrow">
                    {selectedDiagram.type === "video"
                      ? "VIDEO WALKTHROUGH"
                      : selectedDiagram.type === "diagram"
                      ? "ARCHITECTURAL DIAGRAM"
                      : "TECHNICAL RESOURCE"}
                  </span>
                  <h3 className="preview-main-title">{selectedDiagram.name}</h3>
                </div>
                <button
                  type="button"
                  className="close-button preview-close-btn"
                  onClick={closeModal}
                  aria-label="Close preview"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body preview-modal-body" data-lenis-prevent>
                {isLoading ? (
                  <div className="loading-spinner">Loading resource...</div>
                ) : error ? (
                  <div className="error-message">{error}</div>
                ) : selectedDiagram.image?.toLowerCase().endsWith(".pdf") ? (
                  <div className="pdf-preview-container" style={{ width: "100%", height: "520px" }}>
                    <iframe
                      src={getImagePath(selectedDiagram.image)}
                      title={selectedDiagram.name}
                      width="100%"
                      height="100%"
                      style={{ border: "none", borderRadius: "16px", background: "#ffffff" }}
                    />
                  </div>
                ) : selectedDiagram.url ? (
                  <div className="live-demo-preview-container" style={{ width: "100%", height: "520px" }}>
                    <iframe
                      src={selectedDiagram.url}
                      title={selectedDiagram.name}
                      width="100%"
                      height="100%"
                      style={{ border: "none", borderRadius: "16px", background: "#ffffff" }}
                    />
                  </div>
                ) : selectedDiagram.image?.toLowerCase().endsWith(".mp4") ||
                  selectedDiagram.image?.toLowerCase().endsWith(".mov") ||
                  selectedDiagram.image?.toLowerCase().endsWith(".webm") ||
                  selectedDiagram.type === "video" ? (
                  <div className="video-player-wrapper">
                    {selectedDiagram.url ? (
                      <div className="live-demo-preview-container" style={{ width: "100%", height: "520px" }}>
                        <iframe
                          src={selectedDiagram.url}
                          title={selectedDiagram.name}
                          width="100%"
                          height="100%"
                          style={{ border: "none", borderRadius: "16px", background: "#ffffff" }}
                        />
                      </div>
                    ) : (
                      <video
                        controls
                        autoPlay
                        className="modal-video"
                        playsInline
                        poster="/images/products/EngageAI-3D-Studio-Banner.png"
                        onError={(e) => {
                          const container = e.target.parentElement;
                          container.innerHTML = `
                            <div class="video-fallback-stage" style="padding: 40px; text-align: center; background: rgba(15, 23, 42, 0.95); border-radius: 16px; color: #fff;">
                              <div style="font-size: 18px; font-weight: 700; margin-bottom: 8px;">${selectedDiagram.name}</div>
                              <p style="color: #94a3b8; font-size: 14px; margin-bottom: 20px;">${selectedDiagram.desc}</p>
                              <a href="${selectedDiagram.url || 'https://inextlabs.ai'}" target="_blank" rel="noopener noreferrer" style="display: inline-flex; align-items: center; gap: 8px; padding: 12px 24px; background: #F05742; color: #fff; border-radius: 9999px; font-weight: 600; text-decoration: none;">
                                Launch Interactive Demonstration ↗
                              </a>
                            </div>
                          `;
                        }}
                      >
                        <source
                          src={getImagePath(selectedDiagram.image)}
                          type="video/mp4"
                        />
                        Your browser does not support HTML5 video.
                      </video>
                    )}
                  </div>
                ) : selectedDiagram.image?.toLowerCase().endsWith(".png") ||
                  selectedDiagram.image?.toLowerCase().endsWith(".jpg") ||
                  selectedDiagram.image?.toLowerCase().endsWith(".jpeg") ||
                  selectedDiagram.image?.toLowerCase().endsWith(".svg") ? (
                  <div className="diagram-image-container">
                    <BlurImage
                      src={getImagePath(selectedDiagram.image)}
                      alt={selectedDiagram.name}
                      className="modal-diagram-image"
                    />
                  </div>
                ) : selectedDiagram.image?.toLowerCase().endsWith(".xlsx") ? (
                  <div className="excel-table-container">
                    {tableData.length > 0 ? (
                      <table className="excel-table">
                        <thead>
                          <tr>
                            {tableData[0]?.map((cell, idx) => (
                              <th key={idx}>{cell || `Col ${idx + 1}`}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tableData.slice(1).map((row, rowIdx) => (
                            <tr key={rowIdx}>
                              {row.map((cell, cellIdx) => (
                                <td key={cellIdx}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="empty-table-message">
                        No data available in Excel file.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="unsupported-format">
                    <ImageIcon size={48} className="unsupported-icon" />
                    <p>Format not supported for preview.</p>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Resources;
